import { getSnapshot } from 'mobx-state-tree';
import React, { useState } from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  useIntl,
} from 'react-intl';
import { useHistory } from 'react-router-dom';
import Box from 'ui-box';
import {
  Button,
  PageContainer,
  Container,
  majorScale,
  Heading,
  minorScale,
  Icon,
  Tag,
  VirtualTable,
  VirtualTableItem,
  PageLoader,
  ControlGroup,
} from '../../npaper';
import { FormattedUnit } from '../../npaper/FormattedUnit';
import { SearchInput } from '../../npaper/SearchInput';
import { IProduct, Product, ProductType, useStores } from '../../stores';
import { useFilterFetch } from '../../stores/utils/useFilterFetch';
import { CompanySelect } from '../companies/CompanySelect';
import { ImportProductsSideSheet } from './import/ImportProductsSideSheet';
import { CostPriceText } from './PriceTexts';
import { ProductDetailsDialog } from './ProductDetailsDialog';
import { ProductGroupTitle } from './ProductGroupTitle';
import { ProductThumbnail } from './ProductThumbnail';

export const Products = () => {
  const { productStore } = useStores();
  const intl = useIntl();
  const history = useHistory();

  const { items, isLoading, fetchMore, total, filter, setFilter } =
    useFilterFetch<typeof Product>(productStore as any, {
      ordering: 'sku,id',
      filter: { deleted: false },
    });
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [isImportingProduct, setIsImportingProduct] = useState(false);

  const selectedSupplier =
    filter.filter &&
    filter.filter.supplier__in &&
    filter.filter.supplier__in.length > 0
      ? filter.filter.supplier__in[0]
      : undefined;

  return !items ? (
    <PageContainer>
      <PageLoader />
    </PageContainer>
  ) : (
    <PageContainer
      display="flex"
      flexDirection="column"
      paddingBottom="0"
      height="100%"
      maxHeight="100vh"
    >
      <ProductDetailsDialog
        isShown={isCreatingProduct}
        onClose={(product) => {
          setIsCreatingProduct(false);
          if (product) {
            history.push(`/products/${product.id}`);
          }
        }}
      />
      <ImportProductsSideSheet
        isShown={isImportingProduct}
        onClose={() => setIsImportingProduct(false)}
      />
      <Container marginBottom={majorScale(2)} flexGrow={0} flexShrink={0}>
        <Heading size={700} marginBottom={minorScale(3)}>
          Produkter
        </Heading>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <SearchInput
            marginRight={majorScale(1)}
            currentQuery={filter.filter ? filter.filter.search : ''}
            onSearch={(query) => {
              setFilter({
                ...filter,
                filter: { ...filter.filter, search: query },
              });
            }}
          />
          {/*<FilterPopover<IProductSnapshotOut>
            filter={filter.filter}
            onChange={(f) => setFilter({ ...filter, filter: f })}
            filterFields={[
              {
                field: 'name',
                label: 'Navn',
                type: 'string',
              },
              {
                field: 'type',
                label: 'Type',
                type: 'option',
                options: [
                  {
                    id: ProductType.GOOD,
                    label: 'Vare',
                  },
                  {
                    id: ProductType.SERVICE,
                    label: 'Service',
                  },
                  {
                    id: ProductType.PACKAGE,
                    label: 'Pakkeløsning',
                  },
                ],
              },
              {
                field: 'sku',
                label: 'SKU',
                type: 'string',
              },
              {
                field: 'manufacturer_number',
                label: 'Producentnummer',
                type: 'string',
              },
              {
                field: 'retail_price',
                label: 'Udsaglspris',
                type: 'number',
              },
              {
                field: 'tags__name',
                label: 'Tags',
                type: 'tag',
              },
            ]}
          />*/}
          <Box marginLeft="auto" display="flex">
            <ControlGroup>
              <CompanySelect
                filter={{
                  supplier: true,
                }}
                selected={selectedSupplier}
                onSelect={(company) => {
                  setFilter({
                    filter: {
                      ...filter.filter,
                      supplier__in: [company.id],
                    },
                    ordering: filter.ordering,
                  });
                }}
                placeholder={intl.formatMessage({
                  defaultMessage: 'Filter by supplier',
                })}
              />
              {selectedSupplier && (
                <Button
                  iconLeft="cross"
                  onClick={() => {
                    setFilter({
                      filter: {
                        ...filter.filter,
                        supplier__in: undefined,
                      },
                      ordering: filter.ordering,
                    });
                  }}
                />
              )}
            </ControlGroup>

            <Button
              marginX={majorScale(1)}
              iconLeft={<Icon icon="import" />}
              onClick={() => setIsImportingProduct(true)}
            >
              <FormattedMessage defaultMessage="Import" />
            </Button>
            <Button
              iconLeft={<Icon icon="add" />}
              onClick={() => setIsCreatingProduct(true)}
            >
              <FormattedMessage defaultMessage="Create product" />
            </Button>
          </Box>
        </Box>
      </Container>
      <VirtualTable
        isLoading={isLoading}
        items={items}
        total={total}
        rowHeight={48}
        noItemsPlaceholder={
          <Box width="100%" textAlign="center" paddingY={majorScale(6)}>
            <Heading size={500} marginBottom={majorScale(1)}>
              <FormattedMessage defaultMessage="No matching products found" />
            </Heading>
            <p>
              <FormattedMessage
                defaultMessage="Try changing your filters or <link>see all your products</link>"
                values={{
                  link: (chunk: string) => (
                    <span
                      className="link medium"
                      onClick={() =>
                        setFilter({ filter: {}, ordering: filter.ordering })
                      }
                    >
                      {chunk}
                    </span>
                  ),
                }}
              />
            </p>
          </Box>
        }
        header={[
          {
            title: intl.formatMessage({ defaultMessage: 'SKU (id)' }),
            width: 220,
            orderByKeys: ['sku', 'id'],
          },
          {
            title: intl.formatMessage({ defaultMessage: 'Name' }),
            orderByKeys: ['name', 'sku', 'id'],
          },
          {
            title: intl.formatMessage({ defaultMessage: 'Type' }),
            orderByKeys: ['type', 'id'],
          },
          {
            title: intl.formatMessage({
              defaultMessage: 'Manufacturer number',
            }),
            orderByKeys: ['manufacturer_number', 'sku', 'id'],
          },
          {
            title: intl.formatMessage({ defaultMessage: 'Created' }),
            width: 180,
            orderByKeys: ['created_at'],
          },
          {
            title: intl.formatMessage({ defaultMessage: 'Retail price' }),
            align: 'right',
            width: 140,
            orderByKeys: ['retail_price'],
          },
          {
            title: intl.formatMessage({ defaultMessage: 'Cost price' }),
            align: 'right',
            orderByKeys: ['own_cost_price'],
          },
          {
            title: intl.formatMessage({ defaultMessage: 'Group' }),
            orderByKeys: ['group__name', 'name'],
          },
          {
            title: intl.formatMessage({ defaultMessage: 'Stock' }),
            align: 'right',
            orderByKeys: ['stock'],
          },
          {
            title: intl.formatMessage({ defaultMessage: 'Tags' }),
            orderByKeys: ['tags__name', 'sku'],
          },
        ]}
        onRequestMore={async () => fetchMore()}
        renderRow={({ item: product }) => <ProductRow product={product} />}
        ordering={filter.ordering}
        onOrderingChange={(ordering) =>
          setFilter({
            filter: filter.filter,
            ordering,
          })
        }
      />
    </PageContainer>
  );
};

const ProductRow = ({ product }: { product: IProduct }) => (
  <>
    <VirtualTableItem fontWeight={600}>
      <ProductThumbnail product={product} showSKU />
    </VirtualTableItem>
    <VirtualTableItem>{product.name}</VirtualTableItem>
    <VirtualTableItem>
      {product.type === ProductType.GOOD
        ? 'Vare'
        : product.type === ProductType.SERVICE
        ? 'Service'
        : 'Pakkeløsning'}
    </VirtualTableItem>
    <VirtualTableItem className="font-mono">
      {product.manufacturer_number}
    </VirtualTableItem>
    <VirtualTableItem>
      <FormattedDate value={product.created_at} format="default" />
    </VirtualTableItem>
    <VirtualTableItem justifyContent="flex-end" className="font-mono">
      <FormattedNumber
        // eslint-disable-next-line react/style-prop-object
        style="currency"
        currency={product.retail_price_currency}
        value={product.retail_price}
        currencyDisplay="code"
      />
    </VirtualTableItem>
    <VirtualTableItem justifyContent="flex-end" className="font-mono">
      <CostPriceText costPrice={product.cost_price} />
    </VirtualTableItem>
    <VirtualTableItem>
      {getSnapshot(product).group && (
        <ProductGroupTitle productGroupId={getSnapshot(product).group as any} />
      )}
    </VirtualTableItem>
    <VirtualTableItem justifyContent="flex-end" className="font-mono">
      <FormattedUnit unit={product.unit} value={product.stock} />
    </VirtualTableItem>
    <VirtualTableItem>
      {product.tags.map((t) => (
        <Tag key={t} small={true} marginRight={minorScale(1)} intent="info">
          {t}
        </Tag>
      ))}
    </VirtualTableItem>
  </>
);
